import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { useSettings } from "../../context/ThemeContext";
import { Plus, Pencil, Trash2, X, Check, Tag } from "lucide-react";

const EMPTY = {
  code: "",
  discount_type: "percent",
  discount_value: 10,
  active: true,
  usage_limit: 0,
  valid_until: "",
  description: "",
};

export default function AdminCoupons() {
  const { settings } = useSettings();
  const valName = settings?.val_name || "Val";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    try {
      const payload = {
        ...editing,
        discount_value: Number(editing.discount_value),
        usage_limit: Number(editing.usage_limit || 0),
        valid_until: editing.valid_until ? new Date(editing.valid_until + "T23:59:59").toISOString() : null,
      };
      if (editing.id) {
        await api.put(`/admin/coupons/${editing.id}`, payload);
      } else {
        await api.post("/admin/coupons", payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setErr(formatErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cupón?")) return;
    await api.delete(`/admin/coupons/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-coupons">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="bn-eyebrow">Promociones</div>
          <h1 className="bn-heading text-3xl md:text-4xl mt-2">Cupones de descuento</h1>
          <p className="text-sm mt-2" style={{ color: "var(--bn-text-muted)" }}>Crea códigos promocionales. {valName} los sugerirá automáticamente cuando se lo pidas.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="bn-btn-primary inline-flex items-center gap-2" data-testid="new-coupon-btn">
          <Plus size={16} /> Nuevo cupón
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.length === 0 && (
          <div className="md:col-span-3 bn-card p-10 text-center" style={{ color: "var(--bn-text-muted)" }}>
            <Tag size={32} className="mx-auto mb-3" />
            <div>Aún no tienes cupones. Crea tu primero para ofrecer descuentos.</div>
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="bn-card p-5" data-testid={`coupon-${c.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="bn-eyebrow" style={{ color: "var(--bn-accent)" }}>{c.discount_type === "percent" ? `${c.discount_value}% OFF` : `$${c.discount_value} OFF`}</div>
                <div className="bn-heading text-2xl mt-2 tracking-widest">{c.code}</div>
              </div>
              <span className="text-xs px-2 py-1 rounded" style={{ background: c.active ? "rgba(22,163,74,0.12)" : "rgba(100,100,100,0.15)", color: c.active ? "#16a34a" : "#6b7280" }}>
                {c.active ? "Activo" : "Inactivo"}
              </span>
            </div>
            {c.description && <p className="mt-3 text-sm" style={{ color: "var(--bn-text-muted)" }}>{c.description}</p>}
            <div className="mt-4 text-xs space-y-1" style={{ color: "var(--bn-text-muted)" }}>
              <div>Usado: {c.times_used} / {c.usage_limit > 0 ? c.usage_limit : "∞"}</div>
              {c.valid_until && <div>Válido hasta: {new Date(c.valid_until).toLocaleDateString()}</div>}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditing({ ...c, valid_until: c.valid_until ? c.valid_until.slice(0, 10) : "" })} className="bn-btn-ghost inline-flex items-center gap-2 flex-1 justify-center" data-testid={`edit-coupon-${c.code}`}>
                <Pencil size={14} /> Editar
              </button>
              <button onClick={() => remove(c.id)} className="bn-btn-ghost" style={{ color: "#b91c1c", borderColor: "rgba(185,28,28,0.3)" }}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="bn-card w-full max-w-xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6" style={{ borderBottom: "1px solid var(--bn-border)" }}>
              <h2 className="bn-heading text-2xl">{editing.id ? "Editar cupón" : "Nuevo cupón"}</h2>
              <button onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Código (mayúsculas automáticas)">
                <input className="bn-input" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} placeholder="VAL15" data-testid="coupon-code" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Tipo de descuento">
                  <select className="bn-input" value={editing.discount_type} onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })} data-testid="coupon-type">
                    <option value="percent">Porcentaje (%)</option>
                    <option value="amount">Cantidad fija ($)</option>
                  </select>
                </Field>
                <Field label={editing.discount_type === "percent" ? "Valor (%)" : "Valor ($)"}>
                  <input type="number" min="0" step="0.01" className="bn-input" value={editing.discount_value} onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })} data-testid="coupon-value" />
                </Field>
              </div>

              <Field label="Descripción (visible para el admin)">
                <input className="bn-input" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Ej: 15% OFF en yates durante febrero" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Límite de usos (0 = ilimitado)">
                  <input type="number" min="0" className="bn-input" value={editing.usage_limit} onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })} />
                </Field>
                <Field label="Válido hasta (opcional)">
                  <input type="date" className="bn-input" value={editing.valid_until || ""} onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })} />
                </Field>
              </div>

              <label className="flex items-center gap-2 bn-body-font">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Cupón activo
              </label>

              {err && <div className="text-sm p-3" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }}>{err}</div>}

              <div className="flex gap-3 pt-2">
                <button onClick={save} className="bn-btn-primary" data-testid="save-coupon-btn">
                  <Check size={14} /> Guardar
                </button>
                <button onClick={() => setEditing(null)} className="bn-btn-ghost">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="bn-eyebrow block mb-2">{label}</span>
      {children}
    </label>
  );
}
